import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import {
  Heart, ExternalLink, Mountain, ArrowLeft, Copy, Check, ArrowRight,
} from "lucide-react";
import { api } from "../lib/api";
import { withUtm } from "../lib/utm";

/**
 * /share/mountain-rescue — quiet, restrained 3-scene reel about why
 * CampCompass supports Mountain Rescue and how viewers can chip in.
 *
 * Tone intentionally subtle: monochrome palette, no shouting, no
 * fundraiser-style countdown bars. Documentary feel — the work speaks
 * for itself. Two distinct CTAs:
 *   • Donate → JustGiving (utm=reel-donate)
 *   • Install the app → camp-compass.uk (utm=reel-install)
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const APP_URL = withUtm(PUBLIC_URL, { source: "mountain_rescue", medium: "reel", campaign: "mr-reel-install" });
const JUSTGIVING_URL = "https://www.justgiving.com/page/camp-compass?utm_source=mountain_rescue&utm_medium=reel&utm_campaign=mr-reel-donate";

const CAPTION_LONG = `Camping means freedom.

Mountain Rescue is the reason that freedom is also safe.

48 volunteer teams across England and Wales. ~3,500 unpaid men and women. 2,400+ callouts a year. Rescues that never come with a bill — paid for entirely by donations.

If you've ever been up a hill in poor weather, they had your back without you ever knowing.

CampCompass donates a portion of every Pro subscription to Mountain Rescue. If you can spare a few quid, our JustGiving page is in the bio.

🔗 justgiving.com/page/camp-compass
🏕️ camp-compass.uk (1-tap install)

#MountainRescue #UKHiking #UKCamping #CampCompass`;

const CAPTION_SHORT = `Mountain Rescue · ~3,500 volunteers, 2,400 callouts/yr, £0 cost to the rescued.

We support them. You can too. 🔗 in bio.`;

function Scene({ children, testid, label, mono = false }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#666]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col text-white overflow-hidden shadow-[0_4px_0_0_#0e0e0e] bg-[#0e0e0e]"
        style={{ aspectRatio: "9 / 16", maxWidth: 380, fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : undefined }}
        data-testid={testid}
      >
        {children}
      </section>
    </div>
  );
}

function CaptionBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border hairline bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{label}</div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#ff4500] hover:text-[#0e0e0e]"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-xs text-[#333] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}

export default function MountainRescueReel() {
  const [donationTotal, setDonationTotal] = useState(0);

  useEffect(() => {
    api.get("/charity/donations").then(({ data }) => {
      setDonationTotal(Number(data?.total_raised_gbp || 0));
    }).catch(() => { /* fallback to 0 */ });
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="mountain-rescue-reel">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/launch-pack" className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#666] hover:text-[#0e0e0e] mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to print pack
        </Link>
        <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
          <Mountain className="w-3 h-3" /> Mountain Rescue support · subtle pitch
        </div>
        <h1 className="font-display text-4xl tracking-tight mb-2">Mountain Rescue · 3-scene reel · 9:16</h1>
        <p className="text-[#555] mb-6 max-w-2xl">
          Quiet, documentary tone — no shouting, no fundraiser-style hard sell.
          Two CTAs at the end: donate to JustGiving, or install the app (UTM-tagged for attribution).
        </p>

        {/* Scenes */}
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          {/* SCENE 1 — THE SETUP */}
          <Scene label="01 · The thought" testid="mr-scene-1">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_30%,rgba(255,69,0,0.10)_0%,transparent_60%),radial-gradient(circle_at_20%_85%,rgba(255,255,255,0.04)_0%,transparent_55%)]" aria-hidden="true" />
            <div className="relative p-6 pt-10 h-full flex flex-col">
              <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/50 mb-6">
                A quiet thought, before you head up the hill
              </div>
              <h2 className="font-display text-[34px] leading-[1.05] tracking-tight mb-5">
                Camping means freedom.
              </h2>
              <h2 className="font-display text-[28px] leading-[1.1] tracking-tight text-white/85">
                Mountain Rescue is the reason that freedom is also <span className="text-[#ff4500]">safe.</span>
              </h2>
              <div className="mt-auto pb-2">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/40">
                  Camp-compass.uk
                </div>
              </div>
            </div>
          </Scene>

          {/* SCENE 2 — THE WORK */}
          <Scene label="02 · The work" testid="mr-scene-2">
            <div className="relative p-6 pt-10 h-full flex flex-col">
              <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/50 mb-6">
                Every year · Mountain Rescue · England + Wales
              </div>
              <div className="space-y-5 mb-auto">
                <div>
                  <div className="font-display text-[58px] leading-none mono">2,400+</div>
                  <div className="text-[12px] text-white/70 mt-1 leading-snug">
                    Callouts. About six rescues every single day, every day of the year.
                  </div>
                </div>
                <div>
                  <div className="font-display text-[58px] leading-none mono">~3,500</div>
                  <div className="text-[12px] text-white/70 mt-1 leading-snug">
                    Volunteers. Every one of them unpaid. All trained in casualty care, rope, search.
                  </div>
                </div>
                <div>
                  <div className="font-display text-[58px] leading-none mono text-[#ff4500]">£0</div>
                  <div className="text-[12px] text-white/70 mt-1 leading-snug">
                    What you pay if they rescue you. Every team is a charity.
                  </div>
                </div>
              </div>
              <div className="mt-3 pb-2 text-[10px] mono uppercase tracking-[0.22em] text-white/40">
                Source · Mountain Rescue E&W
              </div>
            </div>
          </Scene>

          {/* SCENE 3 — TWO CTAs */}
          <Scene label="03 · Two ways to help" testid="mr-scene-3">
            <div className="relative p-6 pt-10 h-full flex flex-col">
              <div className="text-[10px] mono uppercase tracking-[0.22em] text-white/50 mb-5">
                If you'd like to help, here's how
              </div>
              <h2 className="font-display text-[24px] leading-[1.05] tracking-tight mb-5">
                Two quiet ways to <span className="text-[#ff4500]">say thanks.</span>
              </h2>

              {/* CTA #1 — JustGiving */}
              <div className="border border-white/15 bg-white/5 p-3 mb-3" data-testid="mr-cta-donate">
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-[#ff4500] mb-1">
                  1 · Donate
                </div>
                <div className="font-display text-[15px] leading-tight mb-1.5">
                  JustGiving · in your own time
                </div>
                <div className="text-[10.5px] text-white/65 mb-2 leading-snug">
                  Even £3 covers a volunteer's evening petrol.
                  {donationTotal > 0 && (
                    <span className="block mt-1 text-[#fef3c7]">
                      So far: £{Math.round(donationTotal)} raised through us.
                    </span>
                  )}
                </div>
                <div className="inline-flex items-center gap-1 text-[10px] mono text-white/85">
                  justgiving.com/page/camp-compass <ExternalLink className="w-2.5 h-2.5" />
                </div>
              </div>

              {/* CTA #2 — Install the app */}
              <div className="border border-white/15 bg-white/5 p-3 flex items-center gap-3" data-testid="mr-cta-install">
                <div className="bg-white p-1.5 flex-shrink-0">
                  <QRCodeSVG value={APP_URL} size={56} level="M" bgColor="#ffffff" fgColor="#0e0e0e" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] mono uppercase tracking-[0.18em] text-[#ff4500] mb-1">
                    2 · Use the app
                  </div>
                  <div className="font-display text-[13px] leading-tight mb-1">
                    A bit of every Pro sub goes to MREW
                  </div>
                  <div className="text-[10px] mono text-white/65 flex items-center gap-1">
                    <ArrowRight className="w-2.5 h-2.5" /> camp-compass.uk
                  </div>
                </div>
              </div>

              <div className="mt-auto pb-2 text-[10px] mono uppercase tracking-[0.22em] text-white/40 flex items-center gap-1">
                <Heart className="w-3 h-3 fill-current text-[#ff4500]" /> Camp together · support each other
              </div>
            </div>
          </Scene>
        </div>

        {/* Captions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <CaptionBox label="Long caption · Instagram + Facebook" text={CAPTION_LONG} />
          <CaptionBox label="Short caption · Stories + TikTok" text={CAPTION_SHORT} />
        </div>

        {/* Direct links for the agent to test */}
        <div className="flex flex-wrap gap-3">
          <a
            href={JUSTGIVING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#16a34a] text-white hover:bg-[#15803d] transition-colors text-xs mono uppercase tracking-wider"
            data-testid="mr-link-justgiving"
          >
            <Heart className="w-3.5 h-3.5 fill-current" /> Open JustGiving page <ExternalLink className="w-3 h-3" />
          </a>
          <Link
            to="/charities"
            className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#0e0e0e] text-white hover:bg-[#ff4500] transition-colors text-xs mono uppercase tracking-wider"
            data-testid="mr-link-charities"
          >
            <Mountain className="w-3.5 h-3.5" /> See the Charities page
          </Link>
        </div>
      </div>
    </div>
  );
}
